import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarClock, X } from "lucide-react";

// Live booking calendar — opens inline inside the site's popup modal.
export const CALENDLY_URL = "https://calendly.com/connectbackofthemind/30min";

export default function BookingDrawer({ open, onClose }) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (open) {
      setLoaded(false);
      window.__lenis?.stop?.();
    } else {
      window.__lenis?.start?.();
    }
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-label="Book a session"
        >
          <div
            data-testid="booking-drawer-backdrop"
            className="absolute inset-0 bg-pinedeep/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            data-testid="booking-drawer"
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-3xl overflow-hidden rounded-t-[2rem] bg-cream shadow-2xl sm:rounded-[2rem]"
          >
            <div className="flex items-center justify-between gap-4 px-6 py-4 sm:px-8">
              <div>
                <p className="eyebrow">Book a session</p>
                <p className="mt-1 font-serif text-lg text-ink sm:text-xl">
                  Pick a time that suits you — 30 minutes, on us.
                </p>
              </div>
              <button
                data-testid="booking-drawer-close"
                onClick={onClose}
                aria-label="Close booking dialog"
                className="rounded-full border border-pine/15 p-2 text-pine transition-colors hover:bg-sagelight/60"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="relative h-[68vh] min-h-[460px] bg-white">
              {!loaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                  <CalendarClock className="h-8 w-8 animate-pulse-soft text-pine" />
                  <p className="text-sm text-bodytext">Opening the calendar…</p>
                </div>
              )}
              <iframe
                data-testid="calendly-embed"
                title="Calendly booking calendar"
                src={`${CALENDLY_URL}?hide_gdpr_banner=1`}
                className="h-full w-full border-0"
                onLoad={() => setLoaded(true)}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
